import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Radio,
  ShieldAlert,
  Activity,
} from 'lucide-react';
import { useFleetStatus } from '../lib/hooks';
import type { FleetAgentStatus } from '../lib/api';

const STATE_STYLE: Record<
  string,
  { bg: string; border: string; text: string; label: string }
> = {
  healthy: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/40',
    text: 'text-emerald-400',
    label: 'HEALTHY',
  },
  quiet: {
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
    text: 'text-sky-300',
    label: 'QUIET',
  },
  // legacy alias — treated as quiet for display
  stale: {
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
    text: 'text-sky-300',
    label: 'QUIET',
  },
  dark: {
    bg: 'bg-red-500/15',
    border: 'border-red-500/50',
    text: 'text-red-400',
    label: 'DARK',
  },
  never_seen: {
    bg: 'bg-slate-500/10',
    border: 'border-slate-500/30',
    text: 'text-slate-400',
    label: 'NEVER SEEN',
  },
  degraded: {
    bg: 'bg-orange-500/15',
    border: 'border-orange-500/50',
    text: 'text-orange-400',
    label: 'DEGRADED',
  },
};

const STORAGE_KEY = 'sg_fleet_banner_expanded';

function StatePill({ state }: { state: string }) {
  const s = STATE_STYLE[state] || STATE_STYLE.never_seen;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide border ${s.bg} ${s.border} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

function AgentRow({ a }: { a: FleetAgentStatus }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 py-2 border-b border-white/5 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <StatePill state={a.state} />
          <span className="text-sm font-medium text-white truncate">{a.name}</span>
          {a.domain && <span className="text-xs text-gray-500 font-mono">{a.domain}</span>}
          {a.cause && (
            <span className="text-[10px] uppercase tracking-wide text-orange-300/90 font-mono">
              {a.cause}
            </span>
          )}
        </div>
        {a.detail && <p className="text-xs text-gray-400 mt-1 max-w-2xl">{a.detail}</p>}
      </div>
      <div className="text-right text-xs text-gray-500 shrink-0">
        <div>
          last:{' '}
          <span className="text-gray-300 font-mono">
            {a.lastSeenAt ? new Date(a.lastSeenAt).toLocaleString() : '—'}
          </span>
        </div>
        <div className="mt-0.5">
          {a.activity.sessions1h}s / {a.activity.deceptions1h}d (1h)
        </div>
      </div>
    </div>
  );
}

/**
 * Collapsible fleet health banner.
 * Defaults collapsed when healthy; auto-expands when fleet needs attention.
 * User toggle is remembered in sessionStorage.
 */
export function FleetStatusBanner({ compact = false }: { compact?: boolean }) {
  const { data, isLoading, error } = useFleetStatus();
  const [expanded, setExpanded] = useState(false);
  const [userToggled, setUserToggled] = useState(false);

  // Restore user preference once
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw === '1' || raw === '0') {
        setExpanded(raw === '1');
        setUserToggled(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Auto-expand when unhealthy (unless user explicitly collapsed/expanded)
  useEffect(() => {
    if (!data || userToggled) return;
    const needsAttention =
      !data.ok ||
      data.summary.dark > 0 ||
      data.summary.degraded > 0 ||
      data.summary.integrityFailing ||
      data.summary.integrityWarnings ||
      data.integrity?.failing ||
      (data.integrity?.failures?.length ?? 0) > 0;
    // compact prop forces collapsed start unless attention needed
    setExpanded(needsAttention ? true : !compact ? false : false);
  }, [data, userToggled, compact]);

  const toggle = () => {
    setUserToggled(true);
    setExpanded((v) => {
      const next = !v;
      try {
        sessionStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  if (isLoading && !data) {
    return (
      <div className="mb-6 rounded-xl border border-white/10 bg-sg-card/60 px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
        <Activity className="w-4 h-4 animate-pulse" />
        Checking fleet liveness…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Fleet status unreachable — cannot confirm collection is healthy.</span>
        </div>
        {error && (
          <p className="text-xs text-red-400/80 mt-1 ml-6 font-mono break-all">{error}</p>
        )}
        <p className="text-xs text-red-400/60 mt-1 ml-6">
          If Recent Threats still loads, re-login with the current tenant API key (Settings),
          or wait — fleet integrity can take &gt;10s under load.
        </p>
      </div>
    );
  }

  const { summary, agents, integrity, recentAlerts, ok, computedAt } = data;
  const liveAgents = agents.filter((a) => a.state !== 'never_seen');
  const problemAgents = liveAgents.filter((a) => ['dark', 'degraded'].includes(a.state));
  const quietAgents = liveAgents.filter((a) => a.state === 'quiet' || a.state === 'stale');
  const healthyAgents = liveAgents.filter((a) => a.state === 'healthy');
  const quietN = quietAgents.length;
  const healthyN = healthyAgents.length;
  const darkN = liveAgents.filter((a) => a.state === 'dark').length;
  const degradedN = liveAgents.filter((a) => a.state === 'degraded').length;
  const hasIntegrityNotices = integrity.failing || (integrity.failures?.length ?? 0) > 0;
  const hasDetails =
    hasIntegrityNotices ||
    problemAgents.length > 0 ||
    quietAgents.length > 0 ||
    healthyAgents.length > 0 ||
    recentAlerts.length > 0;

  const bannerClass = ok && !hasIntegrityNotices
    ? 'border-emerald-500/30 bg-emerald-500/5'
    : summary.degraded > 0 || summary.dark > 0 || summary.integrityFailing
      ? 'border-red-500/50 bg-red-500/10 shadow-[0_0_30px_rgba(239,68,68,0.12)]'
      : 'border-amber-500/40 bg-amber-500/10';

  return (
    <div className={`mb-6 rounded-xl border ${bannerClass} overflow-hidden`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="w-full px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          {ok ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          ) : summary.dark > 0 || summary.degraded > 0 || summary.integrityFailing ? (
            <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
          ) : (
            <Radio className="w-5 h-5 text-amber-400 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              {ok ? 'Fleet collecting' : 'Fleet needs attention'}
              {hasDetails &&
                (expanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                ))}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {healthyN} live · {quietN}quiet · {darkN}dark · {degradedN}degraded
              {summary.integrityFailing
                ? ' · INTEGRITY FAIL'
                : summary.integrityWarnings || (integrity.failures?.length ?? 0) > 0
                  ? ' · integrity notice'
                  : ''}
              <span className="text-gray-600 ml-2">
                as of {new Date(computedAt).toLocaleTimeString()}
              </span>
              {!expanded && hasDetails && (
                <span className="text-gray-500 ml-2">· click to expand</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs shrink-0">
          {summary.integrityFailing && (
            <span className="px-2 py-1 rounded bg-red-500/20 text-red-300 border border-red-500/40 font-bold">
              DATA INTEGRITY
            </span>
          )}
          {!summary.integrityFailing &&
            (summary.integrityWarnings || (integrity.failures?.length ?? 0) > 0) && (
            <span className="px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 font-bold">
              NOTICE
            </span>
          )}
          {!ok && summary.integrityFailing && (
            <span className="px-2 py-1 rounded bg-white/5 text-gray-400 font-mono" title="Fleet not fully green (critical integrity or agent dark/degraded)">
              fleet not ok
            </span>
          )}
          {!ok && !summary.integrityFailing && (summary.dark > 0 || summary.degraded > 0) && (
            <span className="px-2 py-1 rounded bg-white/5 text-gray-400 font-mono">
              agent issue
            </span>
          )}
        </div>
      </button>

      {expanded && hasDetails && (
        <div className="border-t border-white/5 px-4 py-3 space-y-4">
          {(integrity.failing || (integrity.failures?.length ?? 0) > 0) && (
            <div
              className={`rounded-lg border p-3 ${
                integrity.failing
                  ? 'border-red-500/30 bg-black/20'
                  : 'border-amber-500/30 bg-black/20'
              }`}
            >
              <div
                className={`text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${
                  integrity.failing ? 'text-red-300' : 'text-amber-300'
                }`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {integrity.failing ? 'Integrity failures' : 'Integrity notices'} (last{' '}
                {integrity.windowMinutes}m · this tenant)
              </div>
              <ul className="space-y-1">
                {integrity.failures.map((f) => (
                  <li
                    key={f.code}
                    className={`text-sm ${
                      f.severity === 'critical' ? 'text-red-200/90' : 'text-amber-100/90'
                    }`}
                  >
                    <span
                      className={`font-mono text-xs mr-2 ${
                        f.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                      }`}
                    >
                      {f.code}
                    </span>
                    {f.message}
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-gray-500 font-mono">
                sessions={integrity.sessionsN} null_fp={integrity.nullFingerprintN} (
                {integrity.nullFingerprintPct.toFixed(1)}%) · deceptions=
                {integrity.deceptionsN} orphan={integrity.orphanDeceptionN}
              </div>
            </div>
          )}

          {problemAgents.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">
                Problem agents
              </div>
              {problemAgents.map((a) => (
                <AgentRow key={a.agentId} a={a} />
              ))}
            </div>
          )}

          {quietAgents.length > 0 && (
            <div>
              <div className="text-xs font-bold text-sky-500/80 uppercase tracking-wide mb-1">
                Quiet (no traffic to observe — not a failure)
              </div>
              {quietAgents.map((a) => (
                <AgentRow key={a.agentId} a={a} />
              ))}
            </div>
          )}

          {healthyAgents.length > 0 && (
            <div>
              <div className="text-xs font-bold text-emerald-500/80 uppercase tracking-wide mb-1">
                Live agents
              </div>
              {healthyAgents.map((a) => (
                <AgentRow key={a.agentId} a={a} />
              ))}
            </div>
          )}

          {recentAlerts.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                Recent transitions
              </div>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {recentAlerts.slice(0, 8).map((al) => (
                  <li key={al.id} className="text-xs text-gray-400">
                    <span className="text-gray-600 font-mono mr-2">
                      {new Date(al.createdAt).toLocaleTimeString()}
                    </span>
                    <span
                      className={
                        al.severity === 'critical'
                          ? 'text-red-400'
                          : al.severity === 'warning'
                            ? 'text-amber-400'
                            : 'text-gray-400'
                      }
                    >
                      {al.message}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
